import { useState } from "react";
import { useQuery } from "@apollo/client";
import Header from "../../../components/common/header";
import Partition from "../../../components/common/partition";
import { UserCoupon } from "../../../__generated__/graphql";
import { gql } from "../../../__generated__";

const GET_USER_COUPONS = gql(`
  query UserCoupons {
    userCoupons {
      id
      userId
      couponId
      isUsed
      usedAt
      expiredAt
      issuedAt
      coupon {
        id
        title
        discountType
        discountValue
        minOrderPrice
        maxDiscountAmount
        maxUsageQuantity
        usageCount
        isSoldOut
      }
    }
  }
`);

const Coupon = () => {
  const [activeTab, setActiveTab] = useState<"available" | "expired">(
    "available"
  );
  const { data } = useQuery(GET_USER_COUPONS as any, {
    fetchPolicy: "network-only",
  });

  // 만료 여부 확인 함수 (사용됨 또는 만료일 지남)
  const isCouponExpired = (userCoupon: UserCoupon) => {
    return (
      userCoupon.isUsed ||
      (userCoupon.expiredAt && new Date(userCoupon.expiredAt) < new Date())
    );
  };

  // 사용 가능한 쿠폰 (만료되지 않은 것, soldout 포함)
  const availableCoupons = data?.userCoupons?.filter(
    (coupon: UserCoupon) => !isCouponExpired(coupon)
  );

  // 만료된 쿠폰
  const expiredCoupons = data?.userCoupons?.filter((coupon: UserCoupon) =>
    isCouponExpired(coupon)
  );

  const displayedCoupons =
    activeTab === "available" ? availableCoupons : expiredCoupons;

  return (
    <div className="w-full bg-white">
      <Header title="나의 쿠폰함" showBackButton />
      <div className="w-full px-4 pt-4">
        <div className="text-black text-lg font-semibold text-center">
          총 {data?.userCoupons?.length}개의 쿠폰
        </div>
      </div>

      {/* 작은 탭 */}
      <div className="w-full px-4 pt-4 pb-2">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab("available")}
            className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors ${
              activeTab === "available"
                ? "bg-[#1562fc] text-white"
                : "bg-[#f4f5f7] text-gray-600"
            }`}
          >
            사용 가능 ({availableCoupons?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab("expired")}
            className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors ${
              activeTab === "expired"
                ? "bg-[#1562fc] text-white"
                : "bg-[#f4f5f7] text-gray-600"
            }`}
          >
            만료됨 ({expiredCoupons?.length || 0})
          </button>
        </div>
      </div>

      <Partition color="light" height="thin" />
      <div className="w-full p-4">
        {displayedCoupons?.length === 0 ? (
          <div className="text-gray-600 text-lg text-center py-8">
            {activeTab === "available"
              ? "사용 가능한 쿠폰이 없습니다."
              : "만료된 쿠폰이 없습니다."}
          </div>
        ) : (
          displayedCoupons?.map((userCoupon: UserCoupon) => {
            const isExpired = isCouponExpired(userCoupon);
            const isSoldOut = userCoupon.coupon.isSoldOut === true;
            return (
              <div
                key={userCoupon.id}
                className={`relative rounded-xl overflow-hidden mb-4 shadow-lg ${
                  isExpired
                    ? "bg-gradient-to-br from-gray-300 to-gray-400 opacity-60"
                    : isSoldOut
                    ? "bg-gradient-to-br from-gray-400 to-gray-500 opacity-80"
                    : "bg-gradient-to-br from-[#1562fc] to-[#58a6ff]"
                }`}
              >
                {/* 쿠폰 카드 내용 */}
                <div className="p-5 text-white">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold mb-2">
                        {userCoupon.coupon.title}
                      </h3>
                      <div className="flex items-baseline gap-2 mb-2">
                        {userCoupon.coupon.discountType === "FIXED" ? (
                          <>
                            <span className="text-3xl font-bold">
                              {userCoupon.coupon.discountValue.toLocaleString()}
                            </span>
                            <span className="text-lg">원</span>
                          </>
                        ) : (
                          <>
                            <span className="text-3xl font-bold">
                              {userCoupon.coupon.discountValue}
                            </span>
                            <span className="text-lg">%</span>
                          </>
                        )}
                        <span className="text-white/80 text-sm">할인</span>
                      </div>
                    </div>
                    <div
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                        isExpired
                          ? "bg-white/30 text-white"
                          : isSoldOut
                          ? "bg-red-500/80 text-white"
                          : "bg-white/20 text-white"
                      }`}
                    >
                      {userCoupon.isUsed
                        ? "사용됨"
                        : isExpired
                        ? "만료됨"
                        : isSoldOut
                        ? "마감"
                        : "사용 가능"}
                    </div>
                  </div>

                  <div className="border-t border-white/20 pt-3 mt-3">
                    <div className="flex flex-col gap-1.5 text-white/90 text-xs">
                      {userCoupon.expiredAt && (
                        <div className="flex items-center gap-2">
                          <span className="text-white/70">만료일:</span>
                          <span className="font-semibold">
                            {new Date(userCoupon.expiredAt).toLocaleString(
                              "ko-KR",
                              {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </span>
                        </div>
                      )}
                      {userCoupon.coupon.discountType === "PERCENT" &&
                        userCoupon.coupon.maxDiscountAmount && (
                          <div className="flex items-center gap-2">
                            <span className="text-white/70">최대 할인:</span>
                            <span className="font-semibold">
                              {userCoupon.coupon.maxDiscountAmount.toLocaleString()}
                              원
                            </span>
                          </div>
                        )}
                      {userCoupon.coupon.maxUsageQuantity !== null &&
                        userCoupon.coupon.maxUsageQuantity !== undefined && (
                          <div className="flex items-center gap-2">
                            <span className="text-white/70">선착순:</span>
                            <span className="font-semibold">
                              {userCoupon.coupon.usageCount || 0}/
                              {userCoupon.coupon.maxUsageQuantity}명
                            </span>
                          </div>
                        )}
                      {userCoupon.coupon.minOrderPrice && (
                        <div className="flex items-center gap-2">
                          <span className="text-white/70">최소 주문:</span>
                          <span className="font-semibold">
                            {userCoupon.coupon.minOrderPrice.toLocaleString()}원
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* 만료된 쿠폰 오버레이 */}
                {isExpired && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <div className="text-white text-lg font-bold">만료됨</div>
                  </div>
                )}
                {/* 마감된 쿠폰 오버레이 */}
                {isSoldOut && !isExpired && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <div className="text-white text-lg font-bold">
                      선착순 마감
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Coupon;
